import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Group, GroupInvite, GroupMember } from '../types';

// ===== コレクション定数 =====
const GROUPS_COLLECTION = 'groups';
const MEMBERS_SUBCOLLECTION = 'members';
const INVITES_SUBCOLLECTION = 'invites';

// ===== エラーハンドリング =====
class GroupServiceError extends Error {
  public code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'GroupServiceError';
    this.code = code;
  }
}

// ===== ヘルパー関数 =====
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const generateInviteToken = (): string => {
  return Math.random().toString(36).substr(2, 12) + Math.random().toString(36).substr(2, 12);
};

// ===== グループ CRUD =====

export const createGroup = async (
  userId: string,
  groupData: { name: string; description?: string; color: string },
  userInfo: { displayName: string; photoURL?: string },
): Promise<Group> => {
  try {
    const groupId = generateId();
    const now = new Date().toISOString();

    const group: Group = {
      id: groupId,
      name: groupData.name,
      description: groupData.description,
      color: groupData.color,
      ownerId: userId,
      createdAt: now,
      updatedAt: now,
    };

    // グループドキュメントを作成
    const groupRef = doc(db, GROUPS_COLLECTION, groupId);
    await setDoc(groupRef, group);

    // 作成者をオーナーとしてメンバーに追加
    const member: GroupMember = {
      id: userId,
      userId: userId,
      role: 'owner',
      joinedAt: now,
      displayName: userInfo.displayName,
      ...(userInfo.photoURL ? { photoURL: userInfo.photoURL } : {}),
    };

    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    await setDoc(memberRef, member);

    return group;
  } catch (error) {
    console.error('Error creating group:', error);
    throw new GroupServiceError('グループの作成に失敗しました');
  }
};

export const getGroup = async (groupId: string): Promise<Group | null> => {
  try {
    const groupRef = doc(db, GROUPS_COLLECTION, groupId);
    const groupSnap = await getDoc(groupRef);

    if (!groupSnap.exists()) {
      return null;
    }

    return groupSnap.data() as Group;
  } catch (error) {
    console.error('Error getting group:', error);
    throw new GroupServiceError('グループの取得に失敗しました');
  }
};

export const updateGroup = async (
  groupId: string,
  updates: Partial<Pick<Group, 'name' | 'description' | 'color'>>,
): Promise<void> => {
  try {
    const groupRef = doc(db, GROUPS_COLLECTION, groupId);
    await updateDoc(groupRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating group:', error);
    throw new GroupServiceError('グループの更新に失敗しました');
  }
};

export const deleteGroup = async (groupId: string): Promise<void> => {
  try {
    // メンバーサブコレクションを削除
    const membersRef = collection(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION);
    const membersSnap = await getDocs(membersRef);
    for (const memberDoc of membersSnap.docs) {
      await deleteDoc(memberDoc.ref);
    }

    // 招待サブコレクションを削除
    const invitesRef = collection(db, GROUPS_COLLECTION, groupId, INVITES_SUBCOLLECTION);
    const invitesSnap = await getDocs(invitesRef);
    for (const inviteDoc of invitesSnap.docs) {
      await deleteDoc(inviteDoc.ref);
    }

    // グループドキュメントを削除
    const groupRef = doc(db, GROUPS_COLLECTION, groupId);
    await deleteDoc(groupRef);
  } catch (error) {
    console.error('Error deleting group:', error);
    throw new GroupServiceError('グループの削除に失敗しました');
  }
};

// ===== メンバー管理 =====

export const getGroupMembers = async (groupId: string): Promise<GroupMember[]> => {
  try {
    const membersRef = collection(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION);
    const membersSnap = await getDocs(membersRef);

    return membersSnap.docs.map((doc) => doc.data() as GroupMember);
  } catch (error) {
    console.error('Error getting group members:', error);
    throw new GroupServiceError('メンバー一覧の取得に失敗しました');
  }
};

export const addGroupMember = async (
  groupId: string,
  userId: string,
  userInfo: { displayName: string; photoURL?: string },
  role: 'owner' | 'member' = 'member',
): Promise<GroupMember> => {
  try {
    const now = new Date().toISOString();

    const member: GroupMember = {
      id: userId,
      userId: userId,
      role: role,
      joinedAt: now,
      displayName: userInfo.displayName,
      ...(userInfo.photoURL ? { photoURL: userInfo.photoURL } : {}),
    };

    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    await setDoc(memberRef, member);

    return member;
  } catch (error) {
    console.error('Error adding group member:', error);
    throw new GroupServiceError('メンバーの追加に失敗しました');
  }
};

export const removeGroupMember = async (groupId: string, userId: string): Promise<void> => {
  try {
    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    await deleteDoc(memberRef);
  } catch (error) {
    console.error('Error removing group member:', error);
    throw new GroupServiceError('メンバーの削除に失敗しました');
  }
};

export const updateMemberRole = async (
  groupId: string,
  userId: string,
  role: 'owner' | 'member',
): Promise<void> => {
  try {
    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    await updateDoc(memberRef, { role });
  } catch (error) {
    console.error('Error updating member role:', error);
    throw new GroupServiceError('メンバーの権限更新に失敗しました');
  }
};

// ===== ユーザーの所属グループ取得（コレクショングループクエリ） =====

export const getUserGroups = async (userId: string): Promise<Group[]> => {
  try {
    // コレクショングループクエリで全グループのmembersサブコレクションを横断検索
    const membersQuery = query(
      collectionGroup(db, MEMBERS_SUBCOLLECTION),
      where('userId', '==', userId),
    );

    const membersSnap = await getDocs(membersQuery);

    // メンバーシップからグループIDを抽出
    const groupIds = membersSnap.docs.map((doc) => {
      // パス: groups/{groupId}/members/{memberId}
      const pathSegments = doc.ref.path.split('/');
      return pathSegments[1]; // groupId
    });

    if (groupIds.length === 0) {
      return [];
    }

    // 各グループの情報を取得
    const groups: Group[] = [];
    for (const groupId of groupIds) {
      const group = await getGroup(groupId);
      if (group) {
        groups.push(group);
      }
    }

    // 更新日時でソート（新しい順）
    return groups.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  } catch (error) {
    console.error('Error getting user groups:', error);
    throw new GroupServiceError('所属グループの取得に失敗しました');
  }
};

// ===== 招待機能 =====

export const createInvite = async (
  groupId: string,
  createdBy: string,
  expiresInHours: number = 24 * 7, // デフォルト7日間
): Promise<GroupInvite> => {
  try {
    const inviteId = generateInviteToken();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expiresInHours * 60 * 60 * 1000);

    const invite: GroupInvite = {
      id: inviteId,
      createdBy: createdBy,
      expiresAt: expiresAt.toISOString(),
    };

    const inviteRef = doc(db, GROUPS_COLLECTION, groupId, INVITES_SUBCOLLECTION, inviteId);
    await setDoc(inviteRef, invite);

    return invite;
  } catch (error) {
    console.error('Error creating invite:', error);
    throw new GroupServiceError('招待リンクの作成に失敗しました');
  }
};

export const getInvite = async (groupId: string, inviteId: string): Promise<GroupInvite | null> => {
  try {
    const inviteRef = doc(db, GROUPS_COLLECTION, groupId, INVITES_SUBCOLLECTION, inviteId);
    const inviteSnap = await getDoc(inviteRef);

    if (!inviteSnap.exists()) {
      return null;
    }

    return inviteSnap.data() as GroupInvite;
  } catch (error) {
    console.error('Error getting invite:', error);
    throw new GroupServiceError('招待情報の取得に失敗しました');
  }
};

export const useInvite = async (
  groupId: string,
  inviteId: string,
  userId: string,
  userInfo: { displayName: string; photoURL?: string },
): Promise<{ success: boolean; error?: string }> => {
  try {
    const invite = await getInvite(groupId, inviteId);

    if (!invite) {
      return { success: false, error: '招待が見つかりません' };
    }

    // 有効期限チェック
    if (new Date(invite.expiresAt) < new Date()) {
      return { success: false, error: '招待リンクの有効期限が切れています' };
    }

    // 既に使用済みかチェック
    if (invite.usedBy) {
      return { success: false, error: 'この招待リンクは既に使用されています' };
    }

    // メンバーとして追加
    await addGroupMember(groupId, userId, userInfo, 'member');

    // 招待を使用済みにマーク
    const inviteRef = doc(db, GROUPS_COLLECTION, groupId, INVITES_SUBCOLLECTION, inviteId);
    await updateDoc(inviteRef, {
      usedBy: userId,
      usedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error using invite:', error);
    throw new GroupServiceError('招待の使用に失敗しました');
  }
};

// ===== 権限チェック =====

export const isGroupMember = async (groupId: string, userId: string): Promise<boolean> => {
  try {
    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    const memberSnap = await getDoc(memberRef);
    return memberSnap.exists();
  } catch (error) {
    console.error('Error checking group membership:', error);
    return false;
  }
};

export const isGroupOwner = async (groupId: string, userId: string): Promise<boolean> => {
  try {
    const memberRef = doc(db, GROUPS_COLLECTION, groupId, MEMBERS_SUBCOLLECTION, userId);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      return false;
    }

    const member = memberSnap.data() as GroupMember;
    return member.role === 'owner';
  } catch (error) {
    console.error('Error checking group ownership:', error);
    return false;
  }
};

// ===== プロフィール同期 =====

/**
 * ユーザーの所属する全グループでメンバー情報（displayName, photoURL）を最新に更新
 * ログイン時に呼び出すことで、Authプロファイルとの同期を保つ
 */
export const syncUserProfileToGroups = async (
  userId: string,
  userInfo: { displayName: string; photoURL?: string },
): Promise<void> => {
  try {
    // ユーザーが所属する全グループのメンバーシップを取得
    const membersQuery = query(
      collectionGroup(db, MEMBERS_SUBCOLLECTION),
      where('userId', '==', userId),
    );

    const membersSnap = await getDocs(membersQuery);

    if (membersSnap.empty) {
      return; // 所属グループがない場合は何もしない
    }

    // 各グループのメンバー情報を更新
    const updatePromises = membersSnap.docs.map(async (memberDoc) => {
      const currentData = memberDoc.data() as GroupMember;

      // 変更がある場合のみ更新
      if (
        currentData.displayName !== userInfo.displayName ||
        currentData.photoURL !== userInfo.photoURL
      ) {
        const updateData: Partial<GroupMember> = {
          displayName: userInfo.displayName,
        };

        // photoURLは値がある場合のみ更新（undefinedを避ける）
        if (userInfo.photoURL) {
          updateData.photoURL = userInfo.photoURL;
        }

        await updateDoc(memberDoc.ref, updateData);
      }
    });

    await Promise.all(updatePromises);
    console.log(`Synced profile to ${membersSnap.docs.length} group(s)`);
  } catch (error) {
    // プロフィール同期の失敗はクリティカルではないのでログのみ
    console.error('Error syncing user profile to groups:', error);
  }
};
